import { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { knowledgeBase } from '@/api/knowledgeBase';
import type { KbArticle, KbAudience } from '@/api/knowledgeBase';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { BookOpen, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

marked.setOptions({ gfm: true, breaks: false });

/**
 * Map a portal role to the SET of KB audiences it may see — CASCADING
 * (Sam, 2026-07-01): each tier sees its own content plus every tier below
 * it. Mirrors backend/src/knowledgeBase/service.ts audiencesForRole — keep
 * the two in sync.
 *
 *   super_admin                    → admin, reseller, customer, end-user
 *   reseller_owner / agent         → reseller, customer, end-user
 *   office_manager / standard_user → customer, end-user
 *   limited_user                   → end-user
 */
function audiencesForRole(role: string): KbAudience[] | null {
  if (role === 'super_admin') return ['admin', 'reseller', 'customer', 'end-user'];
  if (role === 'reseller_owner' || role === 'agent') return ['reseller', 'customer', 'end-user'];
  if (role === 'office_manager' || role === 'standard_user') return ['customer', 'end-user'];
  if (role === 'limited_user') return ['end-user'];
  return null;
}

const AUDIENCE_IDS: readonly KbAudience[] = ['admin', 'reseller', 'customer', 'end-user'];

/**
 * Filter sections of a markdown document to those the viewer's cascade set
 * allows. Sections are delimited by `## {#audience-id}` headings. Content
 * before the first audience-tagged heading (the "preamble" — usually the
 * intro paragraphs) is kept for everyone.
 */
function filterMarkdownByAudience(markdown: string, allowed: KbAudience[] | null): string {
  if (!allowed) return markdown;

  const lines = markdown.split('\n');
  const out: string[] = [];
  let skipping = false;

  for (const line of lines) {
    // Only care about level-2 headings that carry an audience marker.
    const m = /^##\s+\{#([a-z-]+)\}/i.exec(line);
    if (m) {
      const sectionAudience = m[1]!.toLowerCase();
      if (!AUDIENCE_IDS.includes(sectionAudience as KbAudience)) {
        // Unknown marker — keep the section (don't accidentally hide useful content).
        skipping = false;
        out.push(line);
        continue;
      }
      skipping = !allowed.includes(sectionAudience as KbAudience);
      if (!skipping) out.push(line);
      continue;
    }
    // Any other top-level heading (## without audience marker) ends skip mode.
    if (/^##\s/.test(line)) {
      skipping = false;
      out.push(line);
      continue;
    }
    // H1 or --- separators also end any skip in progress.
    if (/^#\s/.test(line) || /^---\s*$/.test(line)) {
      skipping = false;
      out.push(line);
      continue;
    }
    if (!skipping) out.push(line);
  }
  return out.join('\n');
}

export function DocsIndex() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['kbArticles'],
    queryFn: () => knowledgeBase.list(),
  });
  const rows = data?.rows ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Help"
        title="Documentation"
        description="Guides and reference material for using Ivrello. Shown here is the content relevant to your role."
      />
      {isLoading ? (
        <div className="text-sm text-slate-400">Loading articles…</div>
      ) : error ? (
        <div role="alert" className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load documentation.'}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <BookOpen className="mx-auto h-8 w-8 text-slate-300 mb-3" />
            No articles yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {rows.map((a) => <ArticleCard key={a.slug} article={a} />)}
        </div>
      )}
    </>
  );
}

function ArticleCard({ article }: { article: KbArticle }) {
  return (
    <Link
      to={`/docs/${article.slug}`}
      className="group block transition-colors"
    >
      <Card className="h-full hover:border-primary/40 transition-colors">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-base group-hover:text-primary transition-colors">
                {article.title}
              </CardTitle>
              <CardDescription className="mt-1">
                {article.audiences.length > 0 ? article.audiences.join(' · ') : 'All audiences'}
              </CardDescription>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-primary transition-colors shrink-0" />
          </div>
        </CardHeader>
      </Card>
    </Link>
  );
}

// ── Single article view ──────────────────────────────────────────────────────

export function DocsArticle() {
  const { slug } = useParams<{ slug: string }>();
  const me = useCurrentUser();
  if (!slug) return <Navigate to="/docs" replace />;

  const { data: article, isLoading, error } = useQuery({
    queryKey: ['kbArticle', slug],
    queryFn: () => knowledgeBase.get(slug),
  });

  const allowed = audiencesForRole(me.role);

  const rendered = useMemo(() => {
    if (!article?.markdown) return '';
    // Strip sections above the viewer's tier BEFORE rendering to HTML —
    // CASCADE: admin sees everything; reseller sees reseller + customer +
    // end-user; customer roles see customer + end-user; limited users see
    // end-user only. Preamble and un-tagged headings are kept for everyone.
    const filtered = filterMarkdownByAudience(article.markdown, allowed);
    const html = marked.parse(filtered, { async: false }) as string;
    return DOMPurify.sanitize(html);
    // `allowed` derives solely from me.role — depend on the role so the
    // fresh array identity per render doesn't defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.markdown, me.role]);

  return (
    <>
      <div className="mb-4 text-sm">
        <Link to="/docs" className="text-muted-foreground hover:text-foreground transition-colors">
          ← All articles
        </Link>
      </div>
      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : error ? (
        <div role="alert" className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load article.'}
        </div>
      ) : !article ? (
        <div className="text-sm text-muted-foreground">Article not found.</div>
      ) : (
        <article className={cn('max-w-3xl', 'kb-article')}>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">{article.title}</h1>
          <p className="text-xs text-muted-foreground mb-8">
            {allowed ? `Showing content for: ${allowed.join(' · ')}` : 'All audiences'}
          </p>
          {/* eslint-disable-next-line react/no-danger */}
          <div className="prose-kb" dangerouslySetInnerHTML={{ __html: rendered }} />
        </article>
      )}
    </>
  );
}
